import {
  ContentExplorerEntry,
  ContentLibraryFiles,
  ContentLibraryFolders,
  ContentLibraryProcessingMeta,
  ContentLibrarySharedFiles,
  ContentLibrarySortBy,
} from 'features/content-library/types';
import {
  transformFileItem,
  transformFolderItem,
  transformMetaItem,
  transformSharedFolderItem,
  transformAndFilterSharedFiles,
} from 'features/content-library/utils/transformUtils';
import { sortContentLibraryEntries } from 'features/content-library/utils/sortContentLibraryEntries';
import { isPrivateWhiteboard } from './isPrivateWhiteboard';

interface ContentGatheringOptions {
  recursive?: boolean;
  targetFolderId?: string;
  includeSharedFiles?: boolean;
  includeSharedFolderEntry?: boolean;
  canPresentLibrary?: boolean;
  canManagePrivateWhiteboards?: boolean;
  sortBy?: ContentLibrarySortBy;
}

interface ContentGatheringParams {
  folders: ContentLibraryFolders;
  files: ContentLibraryFiles;
  processingMeta: ContentLibraryProcessingMeta;
  sharedFiles?: ContentLibrarySharedFiles;
  options?: ContentGatheringOptions;
}

// Gets all folder IDs in a tree structure starting from a root folder
export const getAllFolderIds = (folders: ContentLibraryFolders, rootFolderId: string): string[] => {
  const folderIds: string[] = [];
  const stack: string[] = [rootFolderId];

  while (stack.length > 0) {
    const folderId = stack.pop()!;
    const currentFolder = folders[folderId];

    if (currentFolder) {
      folderIds.push(folderId);
      stack.push(...currentFolder.childFolderIds);
    }
  }

  return folderIds;
};

export const gatherContentLibraryItems = ({
  folders,
  files,
  processingMeta,
  sharedFiles,
  options = {},
}: ContentGatheringParams): ContentExplorerEntry[] => {
  const {
    recursive = true,
    targetFolderId = 'root',
    includeSharedFiles = false,
    includeSharedFolderEntry = false,
    canPresentLibrary = false,
    canManagePrivateWhiteboards = false,
    sortBy = 'name',
  } = options;

  const items: ContentExplorerEntry[] = [];
  const folderItems: ContentExplorerEntry[] = [];
  const fileItems: ContentExplorerEntry[] = [];
  const uploadItems: ContentExplorerEntry[] = [];

  const folderIds = recursive ? getAllFolderIds(folders, targetFolderId) : [targetFolderId];

  if (includeSharedFolderEntry && targetFolderId === 'root' && sharedFiles) {
    const rootFolder = folders[targetFolderId];
    if (rootFolder) {
      items.push(transformSharedFolderItem(sharedFiles.folder, rootFolder));
    }
  }

  for (const folderId of folderIds) {
    const currentFolder = folders[folderId];
    if (currentFolder) {
      // Add child folders (only direct children for target folder, or all for recursive)
      const childFolderIds =
        folderId === targetFolderId || recursive ? currentFolder.childFolderIds : [];

      for (const childFolderId of childFolderIds) {
        const folder = folders[childFolderId];
        if (folder) {
          folderItems.push(transformFolderItem(folder, currentFolder));
        }
      }

      for (const childFileId of currentFolder.childFileIds) {
        const file = files[childFileId];
        if (file) {
          if (!isPrivateWhiteboard(file) || canManagePrivateWhiteboards) {
            fileItems.push(transformFileItem(file, currentFolder));
          }
        }
      }

      for (const childUploadId of currentFolder.childUploadIds) {
        const meta = processingMeta[childUploadId];
        if (meta) {
          uploadItems.push(transformMetaItem(meta, currentFolder));
        }
      }
    }
  }

  // Uploads stay unsorted at the end: until the transfer ends they have no date and no
  // server-reconciled name to sort by.
  items.push(...sortContentLibraryEntries(folderItems, sortBy));
  items.push(...sortContentLibraryEntries(fileItems, sortBy));
  items.push(...uploadItems);

  if (includeSharedFiles && sharedFiles) {
    const sharedItems = transformAndFilterSharedFiles(
      sharedFiles,
      canPresentLibrary,
      canManagePrivateWhiteboards
    );
    items.push(...sharedItems);
  }

  return items;
};
